import React, { PropsWithChildren, useEffect } from "react";
import { IControlledMutator, Nullable } from "sonobello.utilities.react";

import { GetCurrentStep, Step } from "../../types/Step";
import IPathObserver from "../../routing/types/IPathObserver";
import { IPatient } from "../../types/Entities/Patient";

export interface ICalendarPrerequisitesRecord {
  /** Flag indicating if the prerequisites to obtain a booking calendar are met. */
  isCalendarPrerequisiteMet: boolean;
}

export interface ICalendarControllerProps
  extends IControlledMutator<Nullable<ICalendarPrerequisitesRecord>>, IPathObserver {
  /** The props of {@link IPatient} that are required to determine if the app can allow booking. */
  patient: Pick<IPatient, "id" | "isPreviouslyQualified">;
  /** The flow steps which satisfy calendar prerequisites. */
  calendarPrerequisiteSteps: Step[];
}

/** Suspends rendering children until the prerequisites for the calendar are met.
 * @remarks The prerequisites for accurate OPS calendar acquisition are knowing that a guest exists and has submitted
 * their medical information. We use the 'current step' as a alternate means of asserting this is complete.
 */
const CalendarController: React.FC<PropsWithChildren<ICalendarControllerProps>> = ({
  patient,
  pathName,
  value,
  onChange,
  calendarPrerequisiteSteps,
  children
}) => {
  useEffect(() => {
    const currentStep = GetCurrentStep(pathName);
    if (!value?.isCalendarPrerequisiteMet) {
      onChange({
        isCalendarPrerequisiteMet: Boolean(
          (patient.id && calendarPrerequisiteSteps.some(step => step === currentStep)) || patient.isPreviouslyQualified
        )
      });
    }
  }, [
    patient.id,
    patient.isPreviouslyQualified,
    pathName,
    value?.isCalendarPrerequisiteMet,
    calendarPrerequisiteSteps
  ]);

  return value?.isCalendarPrerequisiteMet ? <>{children}</> : null;
};

export default CalendarController;
